import { useCallback, useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'motion/react';
import { ArrowRight } from './Icons';
import { useTranslation, type Language } from '../contexts/TranslationContext';
import { usePageLoadReady } from '../hooks/usePageLoadReady';
import { playDeferredVideo } from '../utils/deferredVideo';
import { DemoVideoVeil } from './DemoVideoVeil';
import {
  ASSISTANT_ADR_PATH,
  CONSULTANT_AGENT_PATH,
  TRANSPORT_ANALYZER_PATH,
  localizePublicHref,
} from '../utils/publicRoutes';
import { LANDING_SECTION_TITLE_CLASS_NAME, landingSectionClassName } from './landingSectionClassName';
import { BrandLink } from './ui/BrandButton';
import {
  CONSULTANT_AGENT_ARTWORK,
  TRANSPORT_AGENT_ARTWORK,
  type AgentArtworkImageAsset,
} from '../config/agentArtworkImages';
import {
  PRESENTATION_DEMOS,
  getPresentationVideoLayout,
  getPresentationVideoLayoutStyle,
  type PresentationDemo,
} from './landingPresentationDemos';
import './LandingAgentsSection.css';
import './AgentArtwork.css';

const LANDING_AGENTS = [
  {
    key: 'transport',
    href: TRANSPORT_ANALYZER_PATH,
    image: TRANSPORT_AGENT_ARTWORK,
    demo: PRESENTATION_DEMOS[1],
  },
  {
    key: 'consultant',
    href: CONSULTANT_AGENT_PATH,
    image: CONSULTANT_AGENT_ARTWORK,
    demo: PRESENTATION_DEMOS[0],
  },
] as const satisfies ReadonlyArray<{
  key: string;
  href: string;
  image: Record<Language, AgentArtworkImageAsset>;
  demo: PresentationDemo;
}>;

const LANDING_AGENT_IMAGE_SIZES =
  '(min-width: 1200px) 556px, (min-width: 1024px) calc(50vw - 2.75rem), (min-width: 768px) calc(50vw - 2.25rem), (min-width: 640px) calc(100vw - 3rem), calc(100vw - 2rem)';

interface LandingAgentsSectionProps {
  isVisible: boolean;
}

export function LandingAgentsSection({
  isVisible,
}: LandingAgentsSectionProps) {
  const { language, t } = useTranslation();

  return (
    <section
      id="features"
      aria-labelledby="landing-agents-title"
      className={landingSectionClassName({
        tone: 'white',
        className: 'overflow-hidden px-4 py-14 sm:px-6 sm:py-32',
      })}
    >
      <div
        id="features-carousel"
        data-landing-scroll-target
        data-animate
        className={`mx-auto w-full max-w-7xl transition-all duration-1000 motion-reduce:transform-none motion-reduce:transition-none ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}
      >
        <header className="mx-auto mb-10 max-w-5xl text-center sm:mb-16">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.18em] text-[rgb(var(--theme-primary-strong))] sm:text-sm">
            {t('landingAgents.eyebrow')}
          </p>
          <h2 id="landing-agents-title" className={LANDING_SECTION_TITLE_CLASS_NAME}>
            {t('landingAgents.title')}
          </h2>
          <p className="mx-auto max-w-4xl text-base leading-8 text-[rgb(var(--theme-muted))] sm:text-lg">
            {t('landingAgents.description')}
          </p>
        </header>

        <div className="mx-auto grid max-w-6xl gap-x-6 gap-y-12 md:grid-cols-2 lg:gap-x-10">
          {LANDING_AGENTS.map((agent) => (
            <LandingAgentCard key={agent.key} agent={agent} />
          ))}
        </div>

        <div className="mt-8 flex justify-center sm:mt-10">
          <BrandLink href={localizePublicHref(ASSISTANT_ADR_PATH, language)} variant="primary" size="md">
            {t('landingAgents.guideLink')}
            <ArrowRight className="h-5 w-5" />
          </BrandLink>
        </div>
      </div>
    </section>
  );
}

function LandingAgentCard({ agent }: { agent: (typeof LANDING_AGENTS)[number] }) {
  const { language, t } = useTranslation();
  const shouldReduceMotion = useReducedMotion();
  const image = agent.image[language];
  const videoSources = agent.demo.videoSources[language];
  const videoLayout = getPresentationVideoLayout(agent.demo.key, language);
  const videoLayoutStyle = getPresentationVideoLayoutStyle(videoLayout);
  const isPageLoaded = usePageLoadReady();
  const videoRef = useRef<HTMLVideoElement>(null);
  const isDemoRequested = useRef(false);
  const playbackRequest = useRef(0);
  const [isDemoVisible, setIsDemoVisible] = useState(false);

  const playDemo = useCallback((restart = false) => {
    if (!isPageLoaded || shouldReduceMotion !== false) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const video = videoRef.current;
    if (!video) return;

    const request = ++playbackRequest.current;
    isDemoRequested.current = true;
    void playDeferredVideo(video, videoSources, restart).catch(() => {
      if (request !== playbackRequest.current) return;
      isDemoRequested.current = false;
      setIsDemoVisible(false);
    });
  }, [isPageLoaded, shouldReduceMotion, videoSources]);

  const pauseDemo = useCallback(() => {
    playbackRequest.current += 1;
    isDemoRequested.current = false;
    videoRef.current?.pause();
    setIsDemoVisible(false);
  }, []);

  useEffect(() => {
    pauseDemo();
  }, [pauseDemo, shouldReduceMotion, videoSources]);

  return (
    <article
      data-landing-agent={agent.key}
      data-demo-active={isDemoVisible}
      className="landing-agent-card landing-agent-artwork relative flex flex-col"
      onMouseEnter={() => playDemo(true)}
      onMouseLeave={(event) => {
        if (!event.currentTarget.contains(document.activeElement)) pauseDemo();
      }}
      onFocus={() => playDemo(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget) && !event.currentTarget.matches(':hover')) {
          pauseDemo();
        }
      }}
    >
      <div
        className="landing-agent-card-media relative overflow-hidden"
        style={videoLayoutStyle}
      >
        <div className="landing-agent-card-visual agent-artwork-visual absolute inset-0 flex items-center justify-center">
          <picture className="contents">
            <source
              type="image/avif"
              srcSet={image.avifSrcSet}
              sizes={LANDING_AGENT_IMAGE_SIZES}
            />
            <source
              type="image/webp"
              srcSet={image.webpSrcSet}
              sizes={LANDING_AGENT_IMAGE_SIZES}
            />
            <img
              src={image.fallbackSrc}
              alt={t(`landingAgents.${agent.key}.imageAlt`)}
              className="h-full w-full object-cover"
              loading="lazy"
              decoding="async"
              width={image.width}
              height={image.height}
            />
          </picture>
        </div>
        <div className="landing-agent-card-demo absolute inset-0 overflow-hidden bg-[rgb(var(--theme-surface-muted))]">
          <video
            ref={videoRef}
            poster={agent.demo.poster}
            data-video-mp4-src={videoSources.mp4}
            data-video-webm-src={videoSources.webm}
            aria-label={t(agent.demo.videoLabelKey)}
            data-landing-agent-video={agent.key}
            data-loading-policy="on-agent-hover-or-focus"
            onPlaying={() => {
              if (isDemoRequested.current) {
                setIsDemoVisible(true);
              } else {
                videoRef.current?.pause();
              }
            }}
            onPause={() => {
              if (videoRef.current?.paused) setIsDemoVisible(false);
            }}
            onError={pauseDemo}
            width={videoLayout.width}
            height={videoLayout.height}
            muted
            loop
            playsInline
            preload="none"
            disablePictureInPicture
            className="landing-agent-card-video presentation-video-media h-full w-full"
          />
          <DemoVideoVeil />
        </div>
      </div>

      <div className="landing-agent-card-content flex flex-1 flex-col items-start text-left">
        <h3 className="landing-agent-card-title text-2xl font-black leading-tight tracking-[-0.035em] text-[rgb(var(--theme-text))] sm:text-3xl">
          {t(`landingAgents.${agent.key}.title`)}
        </h3>
        <p className="landing-agent-card-description mt-3 max-w-lg text-base leading-7 text-[rgb(var(--theme-muted))] sm:text-lg sm:leading-8">
          {t(`landingAgents.${agent.key}.description`)}
        </p>
        <div className="landing-agent-card-action mt-auto pt-6">
          <span
            aria-hidden="true"
            className="landing-agent-card-cta brand-button brand-button-primary brand-button-md min-h-12 px-6 text-base"
          >
            {t('landingAgents.explore')}
            <ArrowRight className="h-5 w-5" />
          </span>
        </div>
      </div>
      <a
        href={localizePublicHref(agent.href, language)}
        className="landing-agent-card-trigger brand-focus-ring absolute inset-0 z-20 cursor-pointer rounded-3xl border-0 bg-transparent p-0"
        aria-label={`${t('landingAgents.explore')} — ${t(`landingAgents.${agent.key}.title`)}`}
      />
    </article>
  );
}
